import {useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {readEndpoint} from 'redux-json-api'

import {playedSeconds} from '../../../../../../shared_components/audio_player/audioUtils'
import {touchSessionActivity} from '../../../../../../lib/sessionActivity'

/**
 * Plays vault recording uploads one track at a time. Bytes come decrypted
 * through `uploads/:id?download=true` (base64 in the JSON:API payload) and
 * play through an Audio element the hook owns, so unmounting stops playback.
 *
 * `track` is null until something plays, then holds
 * {uploadId, filename, playing, elapsed, duration, played}. `elapsed` is the
 * playhead; `played` is the seconds actually heard, so seeking ahead doesn't
 * count as listening and replaying a part doesn't count it twice.
 */
const useUploadAudio = () => {
  const dispatch = useDispatch()

  const [track, setTrack] = useState(null)
  const audioRef = useRef(null)
  // Identity of the latest play() call: downloads aren't cancelable, so a
  // slower earlier request must not commit its audio/track over a newer one.
  const playRequestRef = useRef(0)
  // Identity of the latest play()/pause tap on the current audio, so a
  // play() the browser settles late can't undo a Pause tapped meanwhile.
  const playAttemptRef = useRef(0)
  const progressFrameRef = useRef(null)

  const syncElapsed = useCallback(audio => {
    setTrack(currentTrack => {
      if (!currentTrack) return currentTrack

      const duration = Number.isFinite(audio.duration) ? audio.duration : currentTrack.duration
      const played = playedSeconds(audio)

      // Same object when nothing moved (paused, buffering): skips the re-render.
      if (currentTrack.elapsed === audio.currentTime && currentTrack.duration === duration && currentTrack.played === played)
        return currentTrack

      return {...currentTrack, elapsed: audio.currentTime, duration, played}
    })
  }, [])

  const stopProgressLoop = useCallback(() => {
    cancelAnimationFrame(progressFrameRef.current)
    progressFrameRef.current = null
  }, [])

  // The `timeupdate` event only fires a few times a second, which makes a
  // waveform progress bar visibly step; painting every frame keeps it fluid.
  const startProgressLoop = useCallback(audio => {
    stopProgressLoop()

    const tick = () => {
      syncElapsed(audio)
      progressFrameRef.current = requestAnimationFrame(tick)
    }

    progressFrameRef.current = requestAnimationFrame(tick)
  }, [stopProgressLoop, syncElapsed])

  useEffect(() => () => {
    stopProgressLoop()
    // Anything still in flight belongs to a surface that is gone: a
    // download must not create and start an Audio nobody can stop, and a
    // play() settling late must not restart the progress loop or set state.
    playRequestRef.current += 1

    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
  }, [stopProgressLoop])

  // Marks a track playing once play() has settled — unless that audio has
  // been replaced or the hook unmounted (then it is paused for good), or the
  // writer tapped Pause (or Play again) before the browser settled the
  // earlier attempt (then the later tap's outcome stands).
  const settlePlaying = useCallback((audio, attemptId) => {
    if (audioRef.current !== audio) {
      audio.pause()

      return
    }

    if (attemptId !== playAttemptRef.current) return

    setTrack(currentTrack => currentTrack && {...currentTrack, playing: true})
    startProgressLoop(audio)
  }, [startProgressLoop])

  const play = useCallback(({uploadId, filename, duration = 0}) => {
    playRequestRef.current += 1
    const requestId = playRequestRef.current

    stopProgressLoop()

    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }

    setTrack({uploadId, filename, playing: false, elapsed: 0, duration, played: 0})

    dispatch(readEndpoint(`uploads/${uploadId}?download=true`))
      .then(response => {
        if (requestId !== playRequestRef.current) return

        const attributes = response.body.data.attributes
        const audio = new Audio(`data:${attributes.filetype};base64,${attributes['decrypted-file']}`)

        // A finished track rewinds to the start (as in the mock), ready to
        // play again from the top. What was heard is taken here too: in a
        // background tab the progress loop's frames are suspended while the
        // audio keeps playing, so its last sample can be far from the end.
        audio.onended = () => {
          audio.currentTime = 0

          // Only the card's current audio speaks for the track: an ended
          // event from an audio a newer play() replaced must not stop the
          // new track's progress loop or mark it paused.
          if (audioRef.current !== audio) return

          stopProgressLoop()
          setTrack(currentTrack => currentTrack && {...currentTrack, playing: false, elapsed: 0, played: playedSeconds(audio)})
        }

        // Listening produces no DOM events, so a long recording played on the
        // story screen looked like 10+ minutes of idleness to the session
        // timeout (TK-3053). timeupdate only fires while audio is actually
        // playing, so each one marks the listener active.
        audio.ontimeupdate = () => touchSessionActivity()

        audioRef.current = audio

        // play() can reject (autoplay/gesture restrictions are common when
        // playback starts after an async fetch), so `playing` only flips on
        // success; a rejection falls through to the catch, which clears the
        // track. Not when Pause interrupted this very play() (browsers reject
        // with AbortError then), or the audio has since been replaced: that
        // rejection is the later tap's doing, and the track stays as it is.
        playAttemptRef.current += 1

        const attemptId = playAttemptRef.current

        return audio.play()
          .then(() => settlePlaying(audio, attemptId))
          .catch(error => {
            if (audioRef.current === audio && attemptId === playAttemptRef.current) throw error
          })
      })
      .catch(() => {
        if (requestId === playRequestRef.current) setTrack(null)
      })
  }, [dispatch, settlePlaying, stopProgressLoop, syncElapsed])

  const togglePlayback = useCallback(() => {
    const audio = audioRef.current

    if (!audio) return

    // Functional updates: the progress loop keeps changing elapsed/duration,
    // so a spread of a closed-over track here could roll those values back.
    if (audio.paused) {
      playAttemptRef.current += 1

      const attemptId = playAttemptRef.current

      audio.play()
        .then(() => settlePlaying(audio, attemptId))
        .catch(() => {
          if (audioRef.current === audio && attemptId === playAttemptRef.current)
            setTrack(currentTrack => currentTrack && {...currentTrack, playing: false})
        })
    } else {
      // Pausing outranks a play() still settling for this audio.
      playAttemptRef.current += 1
      audio.pause()
      stopProgressLoop()
      setTrack(currentTrack => currentTrack && {...currentTrack, playing: false})
    }
  }, [settlePlaying, stopProgressLoop])

  /** Jumps to a fraction (0..1) of the loaded track. */
  const seek = useCallback(fraction => {
    const audio = audioRef.current

    if (!audio || !Number.isFinite(audio.duration)) return

    audio.currentTime = Math.max(0, Math.min(1, fraction)) * audio.duration
    setTrack(currentTrack => currentTrack && {...currentTrack, elapsed: audio.currentTime})
  }, [])

  return {track, play, togglePlayback, seek}
}

export default useUploadAudio
